import { Boxes, FlaskConical, Home, Settings, Workflow } from "lucide-react";
import { MouseEvent, ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";

import {
  AppLauncher,
  AppShell,
  type AppShellBreadcrumbItem,
  NavRailItem,
  NotificationBell,
  SearchInput,
  VersionStamp,
} from "nazare-ui/components";
import { useShellStore, useStoredAuth } from "nazare-ui/utils";

import {
  isCurrentAppLink,
  parseAppLauncherManifest,
} from "../app-launcher-manifest";
import { useGlobalSearchInputProps } from "../global-search-context";
import { runViewTransition } from "../run-view-transition";
import { useSecondaryHeaderContent } from "../secondary-header-context";

type NavEntry = {
  to: string;
  label: string;
  /** Longer text shown only when the rail is expanded — see `<NavRailItem>`'s own doc. */
  expandedLabel?: string;
  /** Breadcrumb text for this route, if different from `label` (e.g. the
   * nav rail's short "QA" vs. the breadcrumb's full "QA Automation"). */
  breadcrumbLabel?: string;
  icon: ReactNode;
};

// Primary, scrollable nav list.
const NAV_ENTRIES: NavEntry[] = [
  { to: "/", label: "Home", icon: <Home /> },
  { to: "/workflows", label: "Workflows", icon: <Workflow /> },
  {
    to: "/qa-automation",
    label: "QA",
    expandedLabel: "QA Automation",
    breadcrumbLabel: "QA Automation",
    icon: <FlaskConical />,
  },
  { to: "/services", label: "Services", icon: <Boxes /> },
];

// Rendered in the footer below the theme toggle and above identity/version —
// chrome-adjacent destinations (just Settings for now), not primary nav.
const FOOTER_NAV_ENTRIES: NavEntry[] = [
  { to: "/settings", label: "Settings", icon: <Settings /> },
];

const APP_LAUNCHER_MANIFEST = parseAppLauncherManifest(
  import.meta.env.VITE_APP_LAUNCHER_MANIFEST,
);
const CURRENT_URL = window.location.href;
const CURRENT_APP =
  APP_LAUNCHER_MANIFEST.find((app) =>
    isCurrentAppLink(app.link, CURRENT_URL),
  ) ?? APP_LAUNCHER_MANIFEST[0];
const APP_LAUNCHER_ITEMS = APP_LAUNCHER_MANIFEST.map((app) => ({
  label: app.name,
  href: app.link,
  icon: <img src={app.icon} alt="" />,
  active: isCurrentAppLink(app.link, CURRENT_URL),
}));

// All routes with a nav entry, used to derive the current page's breadcrumb
// trail below — deliberately just this one flat lookup (no route-tree
// walking) since every nav entry here is a single-level child of "/".
const ALL_NAV_ENTRIES = [...NAV_ENTRIES, ...FOOTER_NAV_ENTRIES];

// `/workflows/*` sub-pages (full-page tables, "maximised" from a dashboard
// card) aren't nav entries themselves — they hang off the "Workflows" nav
// entry with a second breadcrumb level instead. Keyed by pathname, same
// flat-lookup approach as `ALL_NAV_ENTRIES`.
const WORKFLOW_SUBPAGE_LABELS: Record<string, string> = {
  "/workflows/runs": "Runs",
  "/workflows/active": "Active runs",
  "/workflows/definitions": "Workflow definitions",
  "/workflows/scheduled": "Scheduled workflows",
  "/workflows/infra": "Infra schedules",
};

// `/qa-automation/*` sub-pages — same "maximised card" convention as
// `WORKFLOW_SUBPAGE_LABELS` above, just hanging off "QA Automation" instead
// of "Workflows".
const QA_SUBPAGE_LABELS: Record<string, string> = {
  "/qa-automation/recent-runs": "Recent runs",
  "/qa-automation/slowest-tests": "5 slowest tests",
  "/qa-automation/highest-failure-rate": "Tests with the highest failure rate",
};

/**
 * Identity for the shell's `<IdentityControl>`, read from the session the
 * Platform auth bootstrap persists on this origin. The neutral fallback is
 * retained for malformed legacy sessions.
 */
function useIdentity() {
  const auth = useStoredAuth();
  const user = auth?.user as
    | {
        firstName?: string;
        lastName?: string;
        email?: string;
        avatar?: string | null;
      }
    | null
    | undefined;

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const name = fullName || user?.email || "Guest";

  return {
    name,
    subtitle: user?.email && user.email !== name ? user.email : undefined,
    avatarUrl: user?.avatar ?? null,
  };
}

/** `/workflows/runs/:runId` — matches the reference app's own truncated
 * "Run wrun_01KWYRK…" breadcrumb style for a run detail page. */
const RUN_DETAIL_PREFIX = "/workflows/runs/";

function runDetailCrumbLabel(runId: string): string {
  const truncated = runId.length > 14 ? `${runId.slice(0, 14)}…` : runId;
  return `Run ${truncated}`;
}

/**
 * Current page's breadcrumb trail. Most routes are a single level deep (one
 * crumb, looked up against `ALL_NAV_ENTRIES` by exact pathname match).
 * `/workflows/*` "maximised" sub-pages get a second crumb back to
 * "Workflows"; `/workflows/runs/:runId` goes one level deeper still, back
 * through "Runs". Falls back to a generic single crumb for anything that
 * matched none of these (e.g. the 404 catch-all).
 */
function useBreadcrumbItems(): AppShellBreadcrumbItem[] {
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (to: string) => (event: MouseEvent) => {
    event.preventDefault();
    runViewTransition(() => navigate(to));
  };

  if (location.pathname.startsWith(RUN_DETAIL_PREFIX)) {
    const runId = location.pathname.slice(RUN_DETAIL_PREFIX.length);
    return [
      { title: "Workflows", uri: "/workflows", onClick: goTo("/workflows") },
      { title: "Runs", uri: "/workflows/runs", onClick: goTo("/workflows/runs") },
      { title: runDetailCrumbLabel(runId) },
    ];
  }

  const subpageLabel = WORKFLOW_SUBPAGE_LABELS[location.pathname];
  if (subpageLabel) {
    return [
      { title: "Workflows", uri: "/workflows", onClick: goTo("/workflows") },
      { title: subpageLabel },
    ];
  }

  const qaSubpageLabel = QA_SUBPAGE_LABELS[location.pathname];
  if (qaSubpageLabel) {
    return [
      { title: "QA Automation", uri: "/qa-automation", onClick: goTo("/qa-automation") },
      { title: qaSubpageLabel },
    ];
  }

  // `/changelog` isn't a nav rail entry (it hangs off the rail's version
  // stamp, not an icon), so it's not in `ALL_NAV_ENTRIES` — a standalone
  // single crumb, no parent, same as this being a top-level destination.
  if (location.pathname === "/changelog") {
    return [{ title: "Changelog" }];
  }

  const entry = ALL_NAV_ENTRIES.find((item) => item.to === location.pathname);
  return [{ title: entry?.breadcrumbLabel ?? entry?.label ?? "Not found" }];
}

/**
 * Root layout route: the shared `<AppShell>` (nav rail + top header) around
 * an `<Outlet>` for whichever page route matched. The first real consumer
 * of `nazare-ui`'s app-shell components.
 */
export function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useIdentity();
  const breadcrumbItems = useBreadcrumbItems();
  const secondaryHeaderContent = useSecondaryHeaderContent();
  const searchInputProps = useGlobalSearchInputProps();

  // Nav rail expanded/collapsed is app-wide chrome state, so it's backed by
  // `nazare-ui`'s shared shell-state store (not a local/uncontrolled
  // `<AppShell>` default) — this is what lets the rail's collapsed state
  // stay consistent across the app (e.g. multiple mounts, or future
  // consumers of the same store) instead of being reset per-mount.
  const navRailCollapsed = useShellStore((state) => state.navRailCollapsed);
  const setNavRailCollapsed = useShellStore(
    (state) => state.setNavRailCollapsed,
  );

  const renderNavEntry = (entry: NavEntry) => {
    const active =
      entry.to === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(entry.to);

    return (
      <NavRailItem
        key={entry.to}
        icon={entry.icon}
        label={entry.label}
        expandedLabel={entry.expandedLabel}
        active={active}
        href={entry.to}
        onClick={(event) => {
          // Only intercept a plain left-click for client-side navigation —
          // preventDefault() was unconditionally blocking the browser's
          // native cmd/ctrl+click ("open in new tab") and middle-click
          // ("open in background tab") behavior on what's a real <a href>.
          // Let those fall through to the browser untouched.
          const isModifiedClick =
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0;
          if (isModifiedClick) return;
          event.preventDefault();
          runViewTransition(() => navigate(entry.to));
        }}
      />
    );
  };

  const expanded = !navRailCollapsed;

  return (
    <AppShell
      logo={
        <img src={CURRENT_APP.icon} alt="" width="16" height="19" />
      }
      logoLabel={`PCG ${CURRENT_APP.name}`}
      navExpanded={expanded}
      onNavExpandedChange={(next) => setNavRailCollapsed(!next)}
      identityName={identity.name}
      identitySubtitle={identity.subtitle}
      identityAvatarUrl={identity.avatarUrl}
      onIdentityClick={() => navigate("/profile")}
      navItems={NAV_ENTRIES.map(renderNavEntry)}
      footerNavItems={FOOTER_NAV_ENTRIES.map(renderNavEntry)}
      appLauncher={
        <AppLauncher expanded={expanded} items={APP_LAUNCHER_ITEMS} />
      }
      breadcrumbItems={breadcrumbItems}
      search={<SearchInput {...searchInputProps} />}
      notifications={<NotificationBell unreadCount={0} />}
      secondaryHeader={secondaryHeaderContent}
      versionStamp={
        <VersionStamp
          version={__APP_VERSION__}
          expanded={expanded}
          onClick={(event) => {
            const isModifiedClick =
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey ||
              event.button !== 0;
            if (isModifiedClick) return;
            event.preventDefault();
            runViewTransition(() => navigate("/changelog"));
          }}
        />
      }
    >
      <Outlet />
    </AppShell>
  );
}
